import React from 'react'
import styled from 'styled-components'

import {
    VatCalculatorActionedAction,
    VatCalculatorActionedField,
    vatCalculatorActioned,
} from '@/analytics/segment'
import Button from '@/components/button'
import Input from '@/components/input'
import {RadioGroup} from '@/components/radio-group'
import type {Props as RichTextProps} from '@/components/rich-text'
import RichText from '@/components/rich-text'
import {Text} from '@/components/text'
import {useGlobalStrings} from '@/providers/global-strings'
import {Layout} from '@/styles/grid'
import {tokens, colors, radii} from '@/theme'
import mq from '@/utils/media-query'

import {Actions} from './vat-calculator'
import type {Dispatch} from './vat-calculator'

interface Props {
    dispatch: Dispatch
    title: string
    body?: RichTextProps['content']
    amountLabel: string
    vatLabel: string
    addVatLabel: string
    removeVatLabel: string
    totalNetLabel: string
    totalGrossLabel: string
    submitLabel: string
}

export const CalculationForm = ({
    title,
    body,
    amountLabel,
    vatLabel,
    addVatLabel,
    removeVatLabel,
    totalGrossLabel,
    totalNetLabel,
    submitLabel,
    dispatch,
}: Props) => {
    const {copied_to_clipboard} = useGlobalStrings()

    const [total, setTotal] = React.useState(0)
    const [amount, setAmount] = React.useState(0)
    const [vatRate, setVatRate] = React.useState(20)
    const [isVatIncluded, setIsVatIncluded] = React.useState(false)
    const [isAmountEmpty, setIsAmountEmpty] = React.useState(false)
    const [isVatRateEmpty, setIsVatRateEmpty] = React.useState(false)

    const onCalculate = () => {
        setIsAmountEmpty(!amount)
        setIsVatRateEmpty(!vatRate)

        if (!amount || !vatRate) {
            return
        }

        const total = getNetAmount(amount, vatRate, isVatIncluded)
        setTotal(total)

        dispatch({
            type: Actions.ADD_RESULT,
            value: {
                id: Math.random(),
                amount,
                vatRate,
                total,
                isVatIncluded,
            },
        })

        vatCalculatorActioned({
            action: VatCalculatorActionedAction.Completed,
            field: VatCalculatorActionedField.Calculate,
        })
    }

    const handleAmountChange = (value: String) => {
        setAmount(+value)
        vatCalculatorActioned({
            action: VatCalculatorActionedAction.Interacted,
            field: VatCalculatorActionedField.Amount,
        })
    }

    const handleVatRateChange = (value: String) => {
        setVatRate(+value)
        vatCalculatorActioned({
            action: VatCalculatorActionedAction.Interacted,
            field: VatCalculatorActionedField.VatAmount,
        })
    }

    return (
        <Layout $variant="two-asymmetrical" $alignItems="center">
            <div>
                <Heading as="h1" $variant="heading-large">
                    {title}
                </Heading>
                <RichText $color="muted" content={body} />
            </div>
            <Form>
                <InputsContainer>
                    <StyledInput
                        label={amountLabel}
                        type="number"
                        required
                        onChange={handleAmountChange}
                        value={amount || ''}
                        error={isAmountEmpty}
                        autoFocus
                    />
                    <StyledInput
                        label={vatLabel}
                        type="number"
                        required
                        onChange={handleVatRateChange}
                        value={vatRate || ''}
                        error={isVatRateEmpty}
                    />
                </InputsContainer>
                <RadioGroup
                    name="calculation-type"
                    defaultValue="add"
                    options={[
                        {
                            value: 'add',
                            label: addVatLabel,
                        },
                        {
                            value: 'remove',
                            label: removeVatLabel,
                        },
                    ]}
                    onChange={(value) => {
                        setIsVatIncluded(value === 'remove')
                        setTotal(0)
                    }}
                />
                <Button type="button" style={{width: '100%'}} onClick={onCalculate}>
                    {submitLabel}
                </Button>
                <StyledResult
                    label={isVatIncluded ? totalNetLabel : totalGrossLabel}
                    value={total || ''}
                    copyable
                    copiedText={copied_to_clipboard}
                />
            </Form>
        </Layout>
    )
}

const getNetAmount = (amount: number, vatRate: number, isVatIncluded: boolean) => {
    const vatRateDecimal = vatRate / 100
    const result = isVatIncluded ? amount / (1 + vatRateDecimal) : amount + amount * vatRateDecimal

    return Number(result.toFixed(2))
}

const Form = styled.form`
    background-color: ${colors.background.pink};
    border-radius: ${radii.arc20};
    padding: ${tokens.spacing20};
    display: flex;
    flex-direction: column;
    gap: ${tokens.spacing20};

    ${mq.min('md')} {
        padding: ${tokens.spacing40};
    }
`

const Heading = styled(Text)`
    margin-bottom: ${tokens.spacing10};

    ${mq.min('sm')} {
        margin-bottom: ${tokens.spacing20};
    }
`

const InputsContainer = styled.div`
    display: flex;
    gap: ${tokens.spacing20};
`

const StyledInput = styled(Input)`
    background-color: ${colors.background.default};
`

const StyledResult = styled(Input)`
    background-color: ${colors.background.pink};
    border: ${({theme}) => theme.borders.secondary};
`
